import { AnnotationResult } from 'bellmere/types.d';
import { spanTypes } from './ElementTypes';
import { WordBoundary } from './WordBoundary.d';
import { NodeOffset } from './NodeOffset.d';
import rateTextSegments from './rateTextSegments';
import { Sentence } from './TextSegment.d';

const findWordBoundaries = (
  result: AnnotationResult, sentences: Sentence[], documentRoot: Node
): WordBoundary | undefined => {
  // find the best matching sentence
  const ratedSentences = rateTextSegments(sentences, result.sentence, 0.9);
  if (ratedSentences.length === 0) return undefined;

  const closestSentence = typeof result.sentenceIndex !== 'undefined'
    ? ratedSentences.reduce((closest, sentence) => {
      const currentDiff = Math.abs(result.sentenceIndex - sentence.item.startIndex);
      const closestDiff = Math.abs(result.sentenceIndex - closest.item.startIndex);
      return currentDiff < closestDiff ? sentence : closest;
    })
    : ratedSentences[0];

  const sentence = closestSentence.item;

  // find the word boundaries and assign to the result
  const startNodeAndOffset =
    findNodeAndOffset(
      sentence.startElement, sentence.startOffset,
      sentence.endElement, sentence.endOffset,
      result.problemSpan[0], documentRoot
    );
  if (!startNodeAndOffset) return undefined;

  const endNodeAndOffset =
    findNodeAndOffset(
      startNodeAndOffset.node, startNodeAndOffset.offset,
      sentence.endElement, sentence.endOffset,
      result.problemSpan[1] - result.problemSpan[0], documentRoot
    );
  if (!endNodeAndOffset) return undefined;

  return {
    startElement: startNodeAndOffset.node,
    startOffset: startNodeAndOffset.offset,
    endElement: endNodeAndOffset.node,
    endOffset: endNodeAndOffset.offset
  };
};

const findNodeAndOffset = (
  startNode: Node,
  startOffset: number,
  endNode: Node,
  endOffset: number,
  spanStart: number,
  documentRoot: Node
): NodeOffset | undefined => {
  let remainingOffset = spanStart;
  let node = startNode;
  let offset = startOffset;
  let lastWasSpace = true;

  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      for (let i = offset; i < node.textContent.length; i += 1) {
        // if we're past the end something went very wrong.. bail out
        if (node === endNode && i > endOffset) return undefined;

        if (remainingOffset === 0) return { node, offset: i };

        if (node.textContent[i].match(/\s/)) {
          if (!lastWasSpace) remainingOffset -= 1;
          lastWasSpace = true;
        } else {
          remainingOffset -= 1;
          lastWasSpace = false;
        }
      }

      // if we're past the end something went very wrong.. bail out
      if (node === endNode) return undefined;
    }
    node = nextNode(node, documentRoot);
    offset = 0;

    // If we're in a block element something went very wrong.. bail out
    if (node && spanTypes.indexOf(node.nodeName.toLowerCase()) < 0 && node.nodeType !== Node.TEXT_NODE) {
      return undefined;
    }
  }
  return undefined;
};

const nextNode = (node: Node, documentRoot: Node): Node | undefined => {
  let currentNode = node;

  if (currentNode.childNodes.length > 0) {
    return currentNode.childNodes[0];
  } if (currentNode.nextSibling) {
    return currentNode.nextSibling;
  }
  while (currentNode.parentElement !== documentRoot) {
    if (currentNode.parentElement.nextSibling) {
      return currentNode.parentElement.nextSibling;
    }
    currentNode = currentNode.parentElement;
  }
  return undefined;
};

export default findWordBoundaries;
